export interface ParsedFacetValueCategory {
  label: string;
  values?: string[];
  startsWith?: string[];
  contains?: string[];
  regex?: string;
  children?: ParsedFacetValueCategory[];
}

export type ParseErrorHandler = (message: string, error: unknown) => void;

export const parseObjectListValue = <T>(
  value: unknown,
  fallbackValue: T[],
  mapItem: (item: any) => T | null,
  onError?: ParseErrorHandler,
): T[] => {
  if (value === undefined || value === null) {
    return fallbackValue;
  }

  try {
    const parsedValue =
      typeof value === 'string'
        ? JSON.parse(value)
        : Array.isArray(value)
          ? value
          : null;

    if (!Array.isArray(parsedValue)) {
      return fallbackValue;
    }

    const normalizedItems = parsedValue
      .map((item) => mapItem(item))
      .filter(Boolean) as T[];

    return normalizedItems;
  } catch (error) {
    onError?.('Failed to parse object list field:', error);
    return fallbackValue;
  }
};

export const parseOptionalNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsedValue =
    typeof value === 'number' ? value : Number.parseInt(String(value), 10);

  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

export const parseFacetValueCategories = (
  value: unknown,
  onError?: ParseErrorHandler,
): ParsedFacetValueCategory[] | undefined => {
  const normalizeStringArray = (arrayValue: unknown): string[] | undefined => {
    if (!Array.isArray(arrayValue)) {
      return undefined;
    }

    const normalizedValues = arrayValue
      .map((entry) => String(entry).trim())
      .filter(Boolean);

    return normalizedValues.length ? normalizedValues : undefined;
  };

  const parseCategory = (item: unknown): ParsedFacetValueCategory | null => {
    if (!item || typeof item !== 'object' || !('label' in item)) {
      return null;
    }

    const categoryItem = item as Record<string, unknown>;

    // Recursively parse children if present
    let parsedChildren: ParsedFacetValueCategory[] | undefined;
    if (Array.isArray(categoryItem.children)) {
      const childCategories = categoryItem.children
        .map((child) => parseCategory(child))
        .filter(Boolean) as ParsedFacetValueCategory[];
      if (childCategories.length) {
        parsedChildren = childCategories;
      }
    }

    return {
      label: String(categoryItem.label).trim(),
      ...(normalizeStringArray(categoryItem.values)
        ? { values: normalizeStringArray(categoryItem.values) }
        : {}),
      ...(normalizeStringArray(categoryItem.startsWith)
        ? { startsWith: normalizeStringArray(categoryItem.startsWith) }
        : {}),
      ...(normalizeStringArray(categoryItem.contains)
        ? { contains: normalizeStringArray(categoryItem.contains) }
        : {}),
      ...(categoryItem.regex ? { regex: String(categoryItem.regex) } : {}),
      ...(parsedChildren ? { children: parsedChildren } : {}),
    };
  };

  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }

  try {
    const parsedValue = JSON.parse(value);
    if (!Array.isArray(parsedValue)) {
      return undefined;
    }

    const categories = parsedValue
      .map((item) => parseCategory(item))
      .filter(Boolean) as ParsedFacetValueCategory[];

    return categories.length ? categories : undefined;
  } catch (error) {
    onError?.('Failed to parse facet value categories:', error);
    return undefined;
  }
};
